/**
 * Registro y navegación centralizados de escenas. Los bloques siguientes
 * añaden escenas nuevas aquí (clave + import) sin tocar el resto del core:
 * BootScene, main.ts y HubScene no necesitan saber que una escena nueva
 * existe hasta que se registra en SCENE_KEYS / SCENE_LIST.
 */

import Phaser from 'phaser';
import { BootScene } from '@/scenes/BootScene';
import { TitleScene } from '@/scenes/TitleScene';
import { HubScene } from '@/scenes/HubScene';
import { PlaceholderScene } from '@/scenes/PlaceholderScene';
import { HudScene } from '@/scenes/HudScene';
import { MissionScene } from '@/scenes/MissionScene';
import { InteriorCasaScene } from '@/scenes/InteriorCasaScene';
import { MinigameCanastaScene } from '@/scenes/MinigameCanastaScene';
import { MinigameDominadasScene } from '@/scenes/MinigameDominadasScene';
import { MinigamePachangaScene } from '@/scenes/MinigamePachangaScene';
import { LaCasaScene } from '@/scenes/LaCasaScene';
import { CampoFutbolScene } from '@/scenes/CampoFutbolScene';
import { MonteScene } from '@/scenes/MonteScene';
import { BarrioScene } from '@/scenes/BarrioScene';

/** Claves de escena únicas. Usar siempre esta tabla, nunca strings sueltos. */
export const SCENE_KEYS = {
  BOOT: 'BootScene',
  TITLE: 'TitleScene',
  HUB: 'HubScene',
  PLACEHOLDER: 'PlaceholderScene',
  HUD: 'HudScene',
  MISSION: 'MissionScene',
  INTERIOR: 'InteriorCasaScene',
  // Las keys de minijuego coinciden con su id en src/data/minijuegos.ts
  // (MissionScene usa el id para saber si la escena existe).
  MG_CANASTA: 'mg-21-canasta',
  MG_DOMINADAS: 'mg-dominadas',
  MG_PACHANGA: 'mg-pachanga',
  LA_CASA: 'LaCasaScene',
  CAMPO: 'CampoFutbolScene',
  MONTE: 'MonteScene',
  BARRIO: 'BarrioScene',
} as const;

export type SceneKey = (typeof SCENE_KEYS)[keyof typeof SCENE_KEYS];

/** Lista de clases de escena en el orden en que se registran en el Game config. */
export const SCENE_LIST = [
  BootScene,
  TitleScene,
  HubScene,
  PlaceholderScene,
  HudScene,
  MissionScene,
  InteriorCasaScene,
  MinigameCanastaScene,
  MinigameDominadasScene,
  MinigamePachangaScene,
  LaCasaScene,
  CampoFutbolScene,
  MonteScene,
  BarrioScene,
];

/** Mapa de zonas del hub a su escena dedicada. Las zonas que NO aparecen
 *  aquí caen al PLACEHOLDER genérico desde `HubScene`. Ampliar cuando se
 *  implementen escenas reales para el resto de zonas (campo, monte, etc.). */
export const ZONE_SCENE_MAP: Record<string, SceneKey> = {
  'interior-casa': SCENE_KEYS.INTERIOR,
  'la-casa': SCENE_KEYS.LA_CASA,
  'campo-futbol': SCENE_KEYS.CAMPO,
  'el-monte': SCENE_KEYS.MONTE,
  'el-barrio': SCENE_KEYS.BARRIO,
};

/** Datos que recibe PlaceholderScene al navegar a una zona sin escena propia. */
export interface PlaceholderSceneData {
  zoneId: string;
  zoneName: string;
}

/** Datos que recibe MissionScene al lanzar una misión. */
export interface MissionSceneData {
  missionId: string;
}

/** Arranca (reemplazando) la escena `key`, pasando `data` opcional. */
export function goTo(scene: Phaser.Scene, key: SceneKey, data?: object): void {
  scene.scene.start(key, data);
}

/** Lanza la escena `key` en paralelo (overlay) si todavía no está activa. */
export function ensureRunning(scene: Phaser.Scene, key: SceneKey, data?: object): void {
  if (!scene.scene.isActive(key)) {
    scene.scene.launch(key, data);
  }
}
